// Tell the backend which UI language is active, so its error messages,
// activity log and notifications come back in that language. The backend
// reads Accept-Language (see backend/i18n.py); without it, it answers in
// Turkish. The browser's own Accept-Language is the OS/browser language,
// which can differ from the language picked in the app, so set it explicitly.
import { activeLanguage } from '../config/languages';

/**
 * The PC's IANA time zone ("Europe/Istanbul"), sent as X-Timezone: the
 * backend stores UTC and takes "today" (default dates, due checks) from the
 * user's own calendar day (backend/clock.py).
 */
export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}

/** Accept-Language value for the given i18next instance: "en". */
export function requestLanguage(i18n) {
  return activeLanguage(i18n);
}

/** Axios request interceptor body: set Accept-Language unless the caller did. */
export function withLanguageHeader(config, i18n) {
  const headers = config.headers || {};
  const has = typeof headers.has === 'function'
    ? headers.has('Accept-Language')
    : Object.keys(headers).some((h) => h.toLowerCase() === 'accept-language');
  if (!has) {
    if (typeof headers.set === 'function') {
      headers.set('Accept-Language', requestLanguage(i18n));
    } else {
      headers['Accept-Language'] = requestLanguage(i18n);
    }
  }
  const zone = browserTimezone();
  if (zone) {
    if (typeof headers.set === 'function') headers.set('X-Timezone', zone);
    else headers['X-Timezone'] = zone;
  }
  config.headers = headers;
  return config;
}

function isBackendUrl(url, backendUrl, origin) {
  if (!url) return false;
  if (backendUrl && url.startsWith(backendUrl)) return true;
  // Relative "/api/..." or same-origin absolute API URLs.
  if (url.startsWith('/api/')) return true;
  return Boolean(origin) && url.startsWith(`${origin}/api/`);
}

/**
 * Wrap window.fetch so the pages that call the API with fetch() (not axios)
 * also send the UI language. Only requests to the backend get the header;
 * calls that already set Accept-Language are left alone. Idempotent.
 */
export function installFetchLanguage(i18n, {
  target = typeof window !== 'undefined' ? window : null,
  backendUrl = process.env.REACT_APP_BACKEND_URL || '',
} = {}) {
  if (!target || typeof target.fetch !== 'function' || target.fetch.runvyaLanguage) return;
  const originalFetch = target.fetch.bind(target);
  const origin = target.location && target.location.origin;
  const wrapped = (input, init = {}) => {
    const url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
    if (!isBackendUrl(url, backendUrl, origin)) return originalFetch(input, init);
    const headers = new Headers((init && init.headers) || (typeof input === 'object' && input && input.headers) || {});
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', requestLanguage(i18n));
    const zone = browserTimezone();
    if (zone && !headers.has('X-Timezone')) headers.set('X-Timezone', zone);
    return originalFetch(input, { ...init, headers });
  };
  wrapped.runvyaLanguage = true;
  target.fetch = wrapped;
}
